import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { ArrowUpRight, Sparkles, BadgeCheck, FileUp } from "lucide-react";
import { useLocale } from "../../i18n/LocaleContext";

const SAMPLE_MATCH = {
  query: "SKF 6205-2RS — sealed deep groove ball bearing, 25mm",
  matched: "NTN 6205LLU — sealed DGBB, 25×52×15",
  altSavings: "−$184 / unit · +6 wk stock",
};

export default function Hero() {
  const { t } = useLocale();
  const scrollTo = (href) => {
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 md:pt-32 pb-20 md:pb-28 bg-[#020617] overflow-hidden"
    >
      {/* Background composition */}
      <div className="absolute inset-0 -z-10 bg-[#020617]">
        <img
          src={`${process.env.REACT_APP_BACKEND_URL}/api/static/site/warehouse-hero.png`}
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover opacity-20"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#020617]/80 via-[#020617]/95 to-[#020617]" />
        <div className="absolute inset-0 bg-grid-dark opacity-25" />
        {/* Editorial accent glow */}
        <div className="absolute top-1/3 -right-32 h-[420px] w-[420px] rounded-full bg-[#0072CE]/15 blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left column — copy */}
          <div className="lg:col-span-7">
            <div
              data-testid="hero-eyebrow"
              className="inline-flex items-center gap-2.5 px-3 py-1.5 bg-white/[0.04] border border-white/10 rounded-full text-[11px] tracking-[0.22em] uppercase text-slate-300 font-semibold mb-7 animate-fade-up"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#0072CE]" />
              <span>Infosys BPM</span>
              <span className="h-3 w-px bg-white/15" />
              <span>Intelligent Sourcing and Procurement Solution<sup className="ml-0.5">™</sup></span>
              <span className="h-3 w-px bg-white/15" />
              <span>UniBuy™ — AI-Native Digital Procurement</span>
            </div>

            <h1
              data-testid="hero-headline"
              className="font-display text-white text-4xl sm:text-5xl lg:text-[64px] xl:text-[72px] font-semibold tracking-[-0.02em] leading-[1.02] animate-fade-up"
            >
              {t("landing.hero.headline_pre")} <span className="italic font-light text-slate-300">{t("landing.hero.headline_em1")}</span>
              <br className="hidden md:block" />
              {t("landing.hero.headline_mid")} <span className="text-[#7DD3FC]">{t("landing.hero.headline_em2")}</span> {t("landing.hero.headline_post")}
            </h1>

            <p
              className="mt-7 text-lg md:text-xl text-slate-300/85 max-w-2xl leading-relaxed font-light animate-fade-up"
              style={{ animationDelay: "120ms" }}
              data-testid="hero-subheadline"
            >
              {t("landing.hero.subhead")}
            </p>

            <div
              className="mt-9 flex flex-col sm:flex-row gap-3 animate-fade-up"
              style={{ animationDelay: "240ms" }}
            >
              <Button
                onClick={() => scrollTo("#lead-form")}
                data-testid="hero-cta-primary"
                className="bg-white text-[#020617] hover:bg-slate-100 rounded-sm px-7 py-6 text-[15px] font-semibold tracking-tight inline-flex items-center gap-2 group"
              >
                {t("landing.hero.cta_primary")}
                <ArrowUpRight size={18} className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
              </Button>
              <Button
                variant="ghost"
                onClick={() => scrollTo("#topaz")}
                data-testid="hero-cta-secondary"
                className="text-white hover:bg-white/5 hover:text-white rounded-sm px-7 py-6 text-[15px] font-semibold border border-white/15 hover:border-white/40"
              >
                {t("landing.hero.cta_secondary")}
              </Button>
            </div>

            {/* ── ProPay × Infosys thin strip ─────────────────────────── */}
            <Link
              to="/infoshop?otp=1&network_id=JAMES.SMITH&company_name=JDECOFFEE&country_name=Netherlands"
              data-testid="hero-propay-strip"
              className="mt-6 inline-flex items-center gap-3 rounded-full border border-[#00B4D8]/30 bg-[#00B4D8]/[0.08] hover:bg-[#00B4D8]/[0.14] hover:border-[#00B4D8]/55 transition-colors px-4 py-2.5 group animate-fade-up"
              style={{ animationDelay: "300ms" }}
            >
              <FileUp size={14} className="text-[#7DD3FC] shrink-0" />
              <span className="text-[12px] uppercase tracking-[0.18em] font-bold text-[#7DD3FC] shrink-0">
                {t("landing.hero.propay_strip_eyebrow")}
              </span>
              <span className="hidden sm:block h-3 w-px bg-white/20 shrink-0" />
              <span className="text-sm text-slate-200/90 leading-tight hidden sm:block">
                {t("landing.hero.propay_strip_body")}
              </span>
              <ArrowUpRight size={14} className="text-[#7DD3FC] ml-1 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
            </Link>

            {/* Inline proof bar */}
            {/* OMNIMatch AI trust chip — sits just above the proof bar so
                investors/CPOs see the AI capability the moment their eye
                lands on the market-size stats (v3.9.3). Links straight
                into the OMNIMatch AI workbench in demo mode.
                See PRD v3.9.0 for the Sourcing Intelligence Engine details. */}
            <Link
              to="/infoshop/admin/enrichment?demo=1"
              data-testid="hero-omnimatch-badge"
              className="mt-10 group inline-flex items-center gap-2 rounded-full pl-1.5 pr-3.5 py-1.5 border border-white/15 bg-white/[0.04] backdrop-blur-md hover:bg-white/[0.08] hover:border-white/25 transition-all animate-fade-up"
              style={{ animationDelay: "300ms" }}
              aria-label="Open OMNIMatch AI workbench"
            >
              <span
                className="inline-flex h-6 w-6 items-center justify-center rounded-full shadow-inner"
                style={{ background: "linear-gradient(135deg, #7C3AED 0%, #0072CE 100%)" }}
              >
                <Sparkles size={12} className="text-white" />
              </span>
              <span className="font-display font-semibold text-white text-sm tracking-tight">
                OMNIMatch AI
              </span>
              <span className="hidden sm:inline text-[11px] text-slate-400 border-l border-white/10 pl-2.5 ml-0.5">
                Enrich · Match · Save
              </span>
              <ArrowUpRight
                size={13}
                className="text-slate-400 group-hover:text-white group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all"
              />
            </Link>

            <div
              className="mt-12 pt-8 border-t border-white/10 grid grid-cols-3 gap-6 max-w-2xl animate-fade-up"
              style={{ animationDelay: "360ms" }}
              data-testid="hero-proof-bar"
            >
              {[
                { k: "30M+",   v: t("landing.hero.proof.skus_label") },
                { k: "$1.26T", v: t("landing.hero.proof.market_label") },
                { k: "15–23k", v: t("landing.hero.proof.suppliers_label") },
              ].map((s) => (
                <div key={s.k}>
                  <div className="font-display text-2xl md:text-3xl font-semibold text-white tracking-tight">
                    {s.k}
                  </div>
                  <div className="mt-1 text-xs md:text-sm text-slate-400 leading-snug max-w-[180px]">
                    {s.v}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right column — Procurement Intelligence Panel (editorial mock) */}
          <div
            className="lg:col-span-5 animate-fade-up"
            style={{ animationDelay: "180ms" }}
            data-testid="hero-intelligence-panel"
            aria-label="OMNISupply procurement intelligence preview"
          >
            <div className="relative">
              <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-white/15 via-white/5 to-transparent" aria-hidden="true" />
              <div className="relative rounded-2xl bg-[#0B1220]/90 backdrop-blur-xl border border-white/10 p-6 md:p-7">
                {/* Header strip */}
                <div className="flex items-center justify-between border-b border-white/5 pb-4 mb-5">
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                    <span className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-semibold">
                      {t("landing.hero.panel.live")}
                    </span>
                  </div>
                  <Sparkles size={14} className="text-[#7DD3FC]" />
                </div>

                {/* Query line */}
                <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
                  {t("landing.hero.panel.buyer_search")}
                </div>
                <div className="mt-1.5 font-mono text-sm text-slate-200 leading-snug">
                  &ldquo;{SAMPLE_MATCH.query}&rdquo;
                </div>

                {/* Result card */}
                <div className="mt-5 rounded-lg border border-white/10 bg-white/[0.03] p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.18em] text-emerald-400 font-semibold flex items-center gap-1.5">
                        <BadgeCheck size={12} /> {t("landing.hero.panel.recommended")}
                      </div>
                      <div className="mt-1.5 text-white font-medium text-[15px]">
                        {SAMPLE_MATCH.matched}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-emerald-400 font-display font-semibold text-lg">98%</div>
                      <div className="text-[10px] text-slate-500 uppercase tracking-wider">{t("landing.hero.panel.match")}</div>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-white/5 text-xs text-slate-300 font-mono">
                    {SAMPLE_MATCH.altSavings}
                  </div>
                </div>

                {/* Pipeline */}
                <div className="mt-5 grid grid-cols-4 gap-2">
                  {[
                    { l: t("landing.hero.panel.pipeline_match"), v: "98%" },
                    { l: t("landing.hero.panel.pipeline_std"),   v: "OK" },
                    { l: t("landing.hero.panel.pipeline_stock"), v: "6w" },
                    { l: t("landing.hero.panel.pipeline_save"),  v: "$184" },
                  ].map((m) => (
                    <div key={m.l} className="rounded-md bg-white/[0.03] border border-white/10 p-2.5">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">{m.l}</div>
                      <div className="text-sm font-semibold text-white mt-0.5">{m.v}</div>
                    </div>
                  ))}
                </div>

                {/* Footer microcopy */}
                <div className="mt-5 pt-4 border-t border-white/5 flex items-center justify-between">
                  <div className="text-[11px] text-slate-500 font-mono">
                    omnisupply.io · v0.1 · Topaz Fabric
                  </div>
                  <div className="text-[11px] text-slate-500 font-mono">
                    ERP: SAP Ariba
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
